// src/components/modals/NotifyFriendModal.tsx
// Invite friend modal for beta testing - send invitation emails
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext.tsx";
import "./NotifyFriendModal.css";

interface NotifyFriendModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotifyFriendModal: React.FC<NotifyFriendModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();

  // Form state
  const [friendEmail, setFriendEmail] = useState<string>("");
  const [friendName, setFriendName] = useState<string>("");
  const [personalMessage, setPersonalMessage] = useState<string>("");
  const [senderName, setSenderName] = useState<string>(user?.username || "");

  // UI state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFriendEmail("");
      setFriendName("");
      setPersonalMessage("");
      setSenderName(user?.username || "");
      setSubmitError("");
      setSubmitSuccess(false);
    }
  }, [isOpen, user]);

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle submit
  const handleSubmit = async () => {
    // Validation
    if (!friendEmail.trim()) {
      setSubmitError("Please enter your friend's email address.");
      return;
    }

    if (!isValidEmail(friendEmail.trim())) {
      setSubmitError("Please enter a valid email address.");
      return;
    }

    if (!senderName.trim()) {
      setSubmitError("Please enter your name so your friend knows who invited them.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${apiUrl}/api/invites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          friend_email: friendEmail.trim(),
          friend_name: friendName.trim() || undefined,
          personal_message: personalMessage.trim() || undefined,
          sender_name: senderName.trim(),
          sender_email: user?.email || undefined,
          sender_user_id: user?.userId || undefined,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send invitation");
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2500);

    } catch (error: any) {
      console.error("Invitation submission error:", error);
      setSubmitError(error.message || "Failed to send invitation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Success screen
  if (submitSuccess) {
    return (
      <div className="notify-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="notify-modal notify-success">
          <div className="notify-success-icon">&#9993;</div>
          <h2>Invitation Sent!</h2>
          <p>Your invitation has been sent to <strong>{friendEmail}</strong></p>
          <p>They'll receive an email with a link to join Expanded Chess and play with you!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notify-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="notify-modal">
        {/* Header */}
        <div className="notify-header">
          <h2>Invite a Friend</h2>
          <button className="notify-close" onClick={onClose} title="Close">
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="notify-content">
          <div className="notify-intro">
            <span className="invite-icon">&#128140;</span>
            <p>Invite your friends to join Expanded Chess! They'll receive an email with a link to create an account and play with you.</p>
          </div>

          {/* Error */}
          {submitError && (
            <div className="notify-error">{submitError}</div>
          )}

          {/* Friend's Email (Required) */}
          <div className="notify-field">
            <label>Friend's Email Address <span className="required">*</span></label>
            <input
              type="email"
              placeholder="friend@example.com"
              value={friendEmail}
              onChange={(e) => setFriendEmail(e.target.value)}
              maxLength={255}
              autoFocus
            />
          </div>

          {/* Friend's Name (Optional) */}
          <div className="notify-field">
            <label>Friend's Name <span className="optional">(optional)</span></label>
            <input
              type="text"
              placeholder="Their name for a personalized greeting"
              value={friendName}
              onChange={(e) => setFriendName(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Your Name */}
          <div className="notify-field">
            <label>Your Name <span className="required">*</span></label>
            <input
              type="text"
              placeholder="Your name"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Personal Message (Optional) */}
          <div className="notify-field">
            <label>Personal Message <span className="optional">(optional)</span></label>
            <textarea
              placeholder="Add a personal note to your invitation..."
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              rows={3}
              maxLength={500}
            />
            <div className="char-count">{personalMessage.length}/500</div>
          </div>
        </div>

        {/* Actions */}
        <div className="notify-actions">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-send"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Send Invitation"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotifyFriendModal;
